import { getValue } from '../utils/ConfigProvider'

const SCHEMA_ORG_CONTEXT = 'https://schema.org'

/**
 * Create WebSite structured data
 */
const createWebsiteStructuredData = baseUrl => {
  return {
    '@type': 'WebSite',
    name: 'Argos',
    url: baseUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl}/search/{search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  }
}

/**
 * Create BreadcrumbList structured data
 */
const createBreadcrumbStructuredData = (breadcrumbData, currentUrl, baseUrl, showHomeLink, currentPageName) => {
  const { isValidBreadcrumb, breadcrumbAncestors, searchTerm } = breadcrumbData

  // Don't generate breadcrumbs for search pages or invalid breadcrumbs
  if (!isValidBreadcrumb || searchTerm) {
    return null
  }

  const itemListElements = []
  let position = 1

  // Add home link if required
  if (showHomeLink) {
    itemListElements.push({
      '@type': 'ListItem',
      position,
      name: 'Home',
      item: baseUrl
    })
    position += 1
  }

  // Add ancestor breadcrumbs
  if (breadcrumbAncestors && Array.isArray(breadcrumbAncestors)) {
    breadcrumbAncestors.forEach(ancestor => {
      if (ancestor.name && ancestor.path) {
        itemListElements.push({
          '@type': 'ListItem',
          position,
          name: ancestor.name,
          item: `${baseUrl}${ancestor.path}`
        })
        position += 1
      }
    })
  }

  // Add current page
  if (currentPageName && currentUrl) {
    itemListElements.push({
      '@type': 'ListItem',
      position,
      name: currentPageName,
      item: currentUrl
    })
  }

  if (itemListElements.length === 0) {
    return null
  }

  return {
    '@type': 'BreadcrumbList',
    itemListElement: itemListElements
  }
}

/**
 * Create CollectionPage structured data for product listings
 */
const createCollectionPageStructuredData = (pageData, products, currentUrl, numberOfResults, baseUrl) => {
  const { categoryName, searchTerm, pageType } = pageData

  // Generate for browse pages with category name OR search directory (SD) pages with search term
  if (pageType === 'browse' && (!categoryName || searchTerm)) {
    return null
  }

  if (pageType === 'directory' && !searchTerm) {
    return null
  }

  if (pageType !== 'browse' && pageType !== 'directory') {
    return null
  }

  // Flatten nested arrays from splitByRelevancy function
  const flatProducts = Array.isArray(products[0]) ? products.flat() : products ?? []

  // Filter to only include items with product attributes (excludes CMS banners)
  let actualProducts = flatProducts.filter(item => item && item.attributes)

  // For search directory pages, only include direct hits (relevancyRank: 1), excluding related products (relevancyRank: 2)
  if (pageType === 'directory') {
    actualProducts = actualProducts.filter(item => item.attributes.relevancyRank === 1)
  }

  // Don't generate CollectionPage if no products exist
  if (actualProducts.length === 0) {
    return null
  }

  return {
    '@type': 'CollectionPage',
    name: pageType === 'directory' ? `Search results for "${searchTerm}"` : categoryName,
    url: currentUrl,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: numberOfResults || actualProducts.length,
      itemListElement: actualProducts.map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          '@id': `${baseUrl}/product/${product.id}`,
          name: product.attributes.name,
          image: `https://media.4rgos.it/s/Argos/${product.id}_R_SET`,
          brand: {
            '@type': 'Brand',
            name: product.attributes.brand
          },
          offers: {
            '@type': 'Offer',
            price: product.attributes.price.toFixed(2),
            priceCurrency: 'GBP',
            // availability: 'https://schema.org/InStock', - field removed, to be injected dynamically in future ticket
            url: `${baseUrl}/product/${product.id}`
          }
        }
      }))
    }
  }
}

/**
 * Generate JSON-LD structured data for SEO
 */
export const generateStructuredData = props => {
  const {
    hasProducts,
    products,
    searchTerm,
    categoryName,
    isValidBreadcrumb,
    breadcrumbAncestors,
    meta,
    canonicalURL,
    pageType = 'browse',
    showHomeLink
  } = props

  const baseUrl = getValue('canonicalBase')
  const structuredDataItems = []

  structuredDataItems.push(createWebsiteStructuredData(baseUrl))

  const currentPageName = !searchTerm ? categoryName : ''

  // Add breadcrumb navigation
  const breadcrumbLD = createBreadcrumbStructuredData(
    { isValidBreadcrumb, breadcrumbAncestors, searchTerm },
    canonicalURL,
    baseUrl,
    showHomeLink,
    currentPageName
  )
  if (breadcrumbLD) {
    structuredDataItems.push(breadcrumbLD)
  }

  // Add collection page data for browse pages with products OR search directory pages with products
  if (hasProducts && products?.length > 0) {
    const collectionPageLD = createCollectionPageStructuredData(
      { categoryName, searchTerm, pageType },
      products,
      canonicalURL,
      meta?.totalData,
      baseUrl
    )
    if (collectionPageLD) {
      structuredDataItems.push(collectionPageLD)
    }
  }

  return JSON.stringify(
    {
      '@context': SCHEMA_ORG_CONTEXT,
      '@graph': structuredDataItems
    },
    null,
    process.env.NODE_ENV === 'development' ? 2 : 0
  )
}

export default generateStructuredData
